<script setup lang="ts">
import AppDialog from "@/components/common/AppDialog.vue";
import {
	default as QueryConfig,
	QueryConfigForm,
	createQueryConfigForm,
	parseQueryConfigForm,
} from "@/components/query/QueryConfig";
import { useForm } from "../../../../../../../packages/form/src/form";
import WeeklyLimitSelection from "../time-history/WeeklyLimitSelection.vue";
import { CursorDataOptions } from "./cursor-value-data-options";

type ConfigForm = {
	weeklyLimits: CursorDataOptions["weeklyLimits"];
} & QueryConfigForm;

const props = defineProps<{
	query: Partial<CursorDataOptions>;
}>();

const emit = defineEmits<{
	"update:query": [CursorDataOptions];
}>();

const dialog = defineModel<boolean>("dialog");

const { t } = useI18n();

const { useField, setValues, handleSubmit, clear } = useForm<ConfigForm>();

const type = useField("$typeConfig.type");
const subType = useField("$typeConfig.subType");

const { data: exceedanceLimit } = useField("weeklyLimits.exceedance", { initialValue: null });
const { data: warningLimit } = useField("weeklyLimits.warning", { initialValue: null });

whenever(
	() => props.query,
	(data) => {
		if (!data.query) return;
		setValues({
			...createQueryConfigForm(data.query),
			weeklyLimits: data.weeklyLimits,
		});
	},
	{ immediate: true }
);

const save = handleSubmit((form) => {
	dialog.value = false;
	emit("update:query", {
		id: props.query.id || "",
		weeklyLimits: form.weeklyLimits,
		query: parseQueryConfigForm(form),
	});
});

const close = () => {
	clear();
	dialog.value = false;
};
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-150"
		:actionText="t('common.save')"
		@click:action="save"
		@close="close"
	>
		<template #title>
			{{ t("dashboard.queryEditor") }}
		</template>
		<template #content>
			<div class="mx-2 my-4">
				<QueryConfig :useField="useField" showPeriod />
				<WeeklyLimitSelection
					v-model:exceedance="exceedanceLimit"
					v-model:warning="warningLimit"
					class="my-4"
					:type="type.data.value"
					:subType="subType.data.value"
				/>
			</div>
		</template>
	</AppDialog>
</template>
